import { useState } from 'react'
import { useStripe, useElements } from '@stripe/react-stripe-js'
import { PaymentElement } from '@stripe/react-stripe-js'
import { Box, Button, CircularProgress, Alert, FormControlLabel, Checkbox } from '@mui/material'

// Stripe Setup Form Component
export function SetupForm({ onSuccess, onError, onCancel, clientSecret, hideCheckbox = false }) {
  const stripe = useStripe()
  const elements = useElements()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [saveCard, setSaveCard] = useState(true)

  const isBillingMode = !!clientSecret

  const handleSubmit = async event => {
    event.preventDefault()

    if (!stripe || !elements) return

    setIsLoading(true)
    setError(null)

    const { error: submitError } = await elements.submit()

    if (submitError) {
      setError(submitError.message)
      setIsLoading(false)

      return
    }

    if (isBillingMode) {
      // Billing mode: Use confirmSetup (always saves)
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      })

      setIsLoading(false)

      if (confirmError) {
        setError(confirmError.message)
        onError?.(confirmError)
      } else {
        // Pass setupIntent and always save in billing mode
        onSuccess?.(setupIntent, true)
      }
    } else {
      // Purchase mode: Create payment method manually
      const { error: createError, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      })

      setIsLoading(false)

      if (createError) {
        setError(createError.message)
        onError?.(createError)
      } else {
        // Pass the payment method details and saveCard preference
        onSuccess?.(paymentMethod, saveCard)
      }
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        options={{
          layout: {
            type: 'accordion',
            defaultCollapsed: false,
          },
          paymentMethodTypes: ['card'],
        }}
      />
      {!hideCheckbox && !isBillingMode && (
        <FormControlLabel
          control={<Checkbox checked={saveCard} color="primary" onChange={e => setSaveCard(e.target.checked)} />}
          label="Save this card for future purchases"
          sx={{ mt: 2 }}
        />
      )}
      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : null}
      <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
        <Button disabled={isLoading} variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={!stripe || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
          type="submit"
          variant="contained"
        >
          {isLoading ? 'Adding Card...' : 'Add Card'}
        </Button>
      </Box>
    </form>
  )
}
